/* eslint-disable camelcase */
import React, { Component } from 'react';
import ReactTable from 'react-table';
import moment from 'moment';
import { connect } from 'react-redux';
import { ModalRoute } from 'react-router-modal';
import { match as Match } from 'react-router-dom';
import { Location } from 'history';

import { AxiosResponse } from 'axios';
import { flattenBookingsToJobs } from '../../../api/DataHandlers';
import { executeHttpRequest } from '../../../epics/api';

import BookingStartTimeWidget from '../../operations/bookings/BookingStartTimeWidget';
import BookingEndTimeWidget from '../../operations/bookings/BookingEndTimeWidget';
import ConflictTimesContainer from '../../operations/bookings/conflictTimesContainer';
import Tooltip from '../../common/Tooltip';

import history from '../../../history';
import { TPageNotification, TUser, TJobItem, TApiMeta, TApiResponse } from '../../../types';
import { TSelectedBookingSummary, TPayrollFreelancer, TExtraRateOption } from './types';
import { TRootState } from '../../../reducers';
import TimesheetDetailsModal from './TimesheetDetailsModal';

interface PayrollSubComponentProps {
    worker: TPayrollFreelancer;
    setRef: (ref: ReactTable | null) => void;
    onRowCollapse: (id: number) => void;
    id: number;
    table: ReactTable | null;
    notification: TPageNotification;
    user: TUser;
    match: Match;
    location: Location;
    onTimesheetUpdated?: () => void;
    rateOptions: TExtraRateOption[];
    onSelectionChange?: (
        freelancerId: number,
        bookings: TSelectedBookingSummary[],
    ) => void;
    pendingDeselection?: number[];
    onDeselectionProcessed?: (
        freelancerId: number,
        bookingIds: number[],
    ) => void;
    generatedBookings?: number[];
}

interface PayrollSubComponentState {
    loading: boolean;
    content: TJobItem[];
    meta: TApiMeta;
    currentPage: number;
    query: {
        page: number;
        before: string;
        after: string;
        electronic_authorisation?: string;
        timesheet_generated?: string;
        bookings_client?: string;
    };
    selectedRows: number[];
    isTimesheetModalOpen: boolean;
    selectedTimesheetId?: number;
    selectedTimesheetEditable?: boolean;
}

class PayrollSubComponent extends Component<PayrollSubComponentProps, PayrollSubComponentState> {
    row: HTMLSpanElement | null = null;

    private _isMounted = false;

    state: PayrollSubComponentState = {
        loading: false,
        content: [],
        meta: {
            status_code: 0,
            status: '',
            count: 0,
            next: null,
            previous: null,
        },
        currentPage: 0,
        query: {
            page: 1,
            before: '',
            after: '',
            electronic_authorisation: '',
            timesheet_generated: '',
        },
        selectedRows: [],
        isTimesheetModalOpen: false,
        selectedTimesheetId: undefined,
    };

    constructor(props: PayrollSubComponentProps) {
        super(props);
        const currentUrlParams = new URLSearchParams(history.location.search);
        this.state = {
            loading: false,
            content: [],
            meta: {
                status_code: 0,
                status: '',
                count: 0,
                next: null,
                previous: null,
            },
            currentPage: 0,
            query: {
                page: 1,
                before: currentUrlParams.get('bookings_before') || '',
                after: currentUrlParams.get('bookings_after') || '',
                electronic_authorisation:
                    currentUrlParams.get('electronic_authorisation') || '',
                timesheet_generated:
                    currentUrlParams.get('timesheet_generated') || '',
            },
            selectedRows: [],
            isTimesheetModalOpen: false,
            selectedTimesheetId: undefined,
            selectedTimesheetEditable: false,
        };
        if (currentUrlParams.get('bookings_client')) {
            this.state.query.bookings_client =
                currentUrlParams.get('bookings_client') || undefined;
        }
    }

    getDuration = (row: TJobItem) => {
        const { booking } = row;
        const startTime = moment(
            booking?.datetime_started || row.start_datetime,
        );
        const finishTime = moment(
            booking?.datetime_finished || row.end_datetime,
        );
        const duration = finishTime.diff(startTime, 'hours', true);
        return parseFloat(duration.toFixed(2)) || 0;
    };

    getSelectedBookingsDetails = (
        selectedRowIds: number[],
        contentData: TJobItem[] = this.state.content,
    ): TSelectedBookingSummary[] => {
        const selectedRowsData = contentData.filter(row =>
            selectedRowIds.includes(row.id),
        );
        return selectedRowsData.map(row => {
            // Find consultant with highest split value
            const sortedSplits = [...(row.consultant_splits || [])].sort(
                (a, b) => Number(b.split_value) - Number(a.split_value)
            );
            const highestSplit = sortedSplits[0];

            const agentId = highestSplit.agent;
            const agentName = highestSplit.agent_name;
            const agentConsultantCode = highestSplit.consultant_code;

            const extraRates = (row.rates || []).map(rate => ({
                id: rate.id,
                jobRequestId: rate.jobrequest,
                jobReference: row.reference_number,
                extraRateCode: rate.extra_rate_code || '',
                description: rate.description || '',
                duration: 0, // Duration is not provided in rates API response
                freelancerPayPerHour: Number(rate.freelancer_pay_per_hour ?? 0),
                clientPayPerHour: Number(rate.client_pay_per_hour ?? 0),
            }));
            return {
                freelancer: {
                    id: this.props.worker.id,
                    name: this.props.worker.full_name,
                    tempestPayrollNumber: this.props.worker.tempest_payroll_number,
                },
                agent: {
                    id: agentId,
                    name: agentName,
                    consultantCode: agentConsultantCode,
                },
                booking: {
                    id: row.booking.id,
                    reference_number: row.booking.reference_number,
                    jobReference: row.reference_number,
                    rateCode: 'BAS',
                    rateDescription: 'Basic Pay',
                    date: row.date,
                    duration: this.getDuration(row),
                    freelancerPayRate: row.booking.freelancer_pay_per_hour,
                    clientPayRate: row.booking.client_pay_per_hour,
                },
                client: {
                    id: row.client_full.id,
                    name:
                        row.client_full.company_name,
                    referenceNumber: row.client_full?.referral_code,
                },
                agency: {
                    division:
                        row.managing_agency_details.division,
                    department:
                        row.managing_agency_details.department ,
                },
                title: row.title,
                extraRates: extraRates.length ? extraRates : undefined,
                employerReference: 'BERRY',
            };
        });
    };

    openTimesheetModal = (timesheetId?: number | null, allowEdit?: boolean) => {
        if (!timesheetId) return;
        this.setState({
            isTimesheetModalOpen: true,
            selectedTimesheetId: timesheetId,
            selectedTimesheetEditable: allowEdit,
        });
    };

    closeTimesheetModal = () => {
        this.setState({
            isTimesheetModalOpen: false,
            selectedTimesheetId: undefined,
        });
    };

    getClientTimesheetEligibility = (row: TJobItem) => {
        const clientData = row.client_full;

        const clientElectronicAuthorisation =
            clientData?.electronic_authorisation === true;
        const clientHasReferralCode =
            !!clientData?.referral_code &&
            !clientData.referral_code.toLowerCase().startsWith('ref');
        const clientNotOnStop = clientData?.on_stop === false;
        const clientHasApprovers = (clientData?.approvers?.length || 0) > 0;

        const isEligible =
            clientElectronicAuthorisation &&
            clientHasReferralCode &&
            clientNotOnStop &&
            clientHasApprovers;

        return {
            clientData,
            clientElectronicAuthorisation,
            clientHasReferralCode,
            clientNotOnStop,
            clientHasApprovers,
            isEligible,
        };
    };

    toggleRowSelection = (id: number) => {
        this.setState(
            prevState => {
                const newSelectedRows = prevState.selectedRows.includes(id)
                    ? prevState.selectedRows.filter(rowId => rowId !== id)
                    : [...prevState.selectedRows, id];
                return { selectedRows: newSelectedRows };
            },
            () => {
                if (this.props.onSelectionChange) {
                    const selectedDetails =
                        this.getSelectedBookingsDetails(this.state.selectedRows);
                    this.props.onSelectionChange(
                        this.props.worker.id,
                        selectedDetails,
                    );
                }
            },
        );
    };

    toggleSelectAll = () => {
        this.setState(
            prevState => {
                // Filter out bookings that already have timesheets or were generated
                const selectableRows = prevState.content.filter(
                    (row: TJobItem) =>
                        row.booking?.timesheet_id == null &&
                        row.booking?.timesheet_number == null &&
                        !this.props.generatedBookings?.includes(
                            row.booking?.id,
                        ) &&
                        this.getClientTimesheetEligibility(row).isEligible,
                );

                const newSelectedRows =
                    prevState.selectedRows.length === selectableRows.length
                        ? []
                        : selectableRows.map((row: TJobItem) => row.id);
                return { selectedRows: newSelectedRows };
            },
            () => {
                if (this.props.onSelectionChange) {
                    const selectedDetails =
                        this.getSelectedBookingsDetails(this.state.selectedRows);
                    this.props.onSelectionChange(
                        this.props.worker.id,
                        selectedDetails,
                    );
                }
            },
        );
    };

    removeSelectedRows = (idsToRemove: number[]) => {
        this.setState(
            prevState => {
                const updatedSelection = prevState.selectedRows.filter(
                    id => !idsToRemove.includes(id),
                );
                return { selectedRows: updatedSelection };
            },
            () => {
                if (this.props.onSelectionChange) {
                    const selectedDetails = this.getSelectedBookingsDetails(
                        this.state.selectedRows,
                    );
                    this.props.onSelectionChange(
                        this.props.worker.id,
                        selectedDetails,
                    );
                }
                if (this.props.onDeselectionProcessed) {
                    this.props.onDeselectionProcessed(
                        this.props.worker.id,
                        idsToRemove,
                    );
                }
            },
        );
    };


    componentDidMount() {
        this._isMounted = true;
        this.props.setRef(this as any);
        this.getData(this.buildQueryString(this.state.query));
        if (this.row) {
            this.row.scrollIntoView({ behavior: 'smooth' });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.props.setRef(null);
        if (this.props.onSelectionChange) {
            this.props.onSelectionChange(this.props.worker.id, []);
        }
    }

    componentDidUpdate(prevProps: PayrollSubComponentProps) {
        if (
            this.props.pendingDeselection &&
            this.props.pendingDeselection !== prevProps.pendingDeselection &&
            this.props.pendingDeselection.length
        ) {
            this.removeSelectedRows(this.props.pendingDeselection);
        }
    }

    reloadSubData = () => {
        this.getData(this.buildQueryString(this.state.query));
    };

    updateBookingsWithTimesheet = (
        bookingIds: number[],
        timesheetId: number,
        timesheetNumber?: string,
        timesheetStatus?: string
    ) => {
        this.setState(prevState => {
            // Find row IDs that match the booking IDs
            const rowIdsToUncheck = prevState.content
                .filter((row: TJobItem) => bookingIds.includes(row.booking?.id))
                .map((row: TJobItem) => row.id);

            return {
                content: prevState.content.map((row: TJobItem) => {
                    if (bookingIds.includes(row.booking?.id)) {
                        return {
                            ...row,
                            booking: {
                                ...row.booking,
                                timesheet_id: timesheetId,
                                timesheet_number: timesheetNumber || null,
                                timesheet_status: timesheetStatus || null,
                            },
                        };
                    }
                    return row;
                }),
                // Uncheck the disabled bookings
                selectedRows: prevState.selectedRows.filter(
                    rowId => !rowIdsToUncheck.includes(rowId)
                ),
            };
        }, () => {
            if (this.props.onSelectionChange) {
                const selectedDetails = this.getSelectedBookingsDetails(this.state.selectedRows);
                this.props.onSelectionChange(this.props.worker.id, selectedDetails);
            }
        });
    };

    buildQueryString = (query: PayrollSubComponentState['query']) => {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        return params.toString();
    };

    getData = (queryString: string) => {
        const { worker } = this.props;
        const { currentPage } = this.state;
        if (!this._isMounted) return Promise.resolve();

        this.setState({ loading: true });
        let latestSelectedRows: number[] = [];
        return executeHttpRequest(
            `jobs/?freelancer_id=${worker.id}&${queryString}`,
        )
            .then((response: AxiosResponse<TApiResponse<TJobItem[]>>) => {
                if (!this._isMounted) return;

                const flatData = flattenBookingsToJobs(response.data) as { content: TJobItem[]; meta: TApiMeta };
                const cloneContent = [...flatData.content];
                let newContent = cloneContent;
                if (flatData.content.length > 10) {
                    const firstHalf = cloneContent.splice(0, 10);
                    if (currentPage % 2 === 0) {
                        newContent = firstHalf;
                    }
                }
                this.setState(
                    prevState => {
                        const availableRowIds = newContent.map(row => row.id);
                        latestSelectedRows = prevState.selectedRows.filter(
                            rowId => availableRowIds.includes(rowId),
                        );
                        return {
                            meta: flatData.meta,
                            content: newContent,
                            loading: false,
                            selectedRows: latestSelectedRows,
                        };
                    },
                    () => {
                        if (this.props.onSelectionChange) {
                            const selectedDetails =
                                this.getSelectedBookingsDetails(
                                    latestSelectedRows,
                                    newContent,
                                );
                            this.props.onSelectionChange(
                                this.props.worker.id,
                                selectedDetails,
                            );
                        }
                    },
                );
            })
            .catch(() => {
                if (this._isMounted) {
                    this.setState({ loading: false });
                }
            });
    };

    getNoDataProps = () => ({
        onClick: () => {
            this.props.onRowCollapse(this.props.id);
        },
    });

    getProps = () => ({
        style: {
            background: 'transparent',
        },
    });

    getTrProps = () => ({
        style: {
            background: 'transparent',
        },
    });

    getTdProps = () => ({
        style: {
            lineHeight: '45px',
        },
    });

    onPageChange = (pageIndex: number) => {
        const pageToFetch = Math.floor(pageIndex / 2) + 1;
        this.setState(
            prevState => ({
                currentPage: pageIndex,
                query: {
                    ...prevState.query,
                    page: pageToFetch,
                },
            }),
            () => {
                this.getData(this.buildQueryString(this.state.query));
            },
        );
    };

    render() {
        const { content, meta, currentPage, loading, selectedRows } = this.state;

        const columns = [
            {
                id: 'checkbox',
                accessor: '',
                Header: () => {
                    const selectableRows = content.filter(
                        (row: TJobItem) =>
                            row.booking?.timesheet_id == null &&
                            row.booking?.timesheet_number == null &&
                            !this.props.generatedBookings?.includes(
                                row.booking?.id,
                            ),
                    );
                    const allSelectableSelected =
                        selectableRows.length > 0 &&
                        selectedRows.length === selectableRows.length &&
                        selectableRows.every((row: TJobItem) =>
                            selectedRows.includes(row.id),
                        );

                    return (
                        <input
                            type="checkbox"
                            checked={allSelectableSelected}
                            onChange={this.toggleSelectAll}
                            disabled={selectableRows.length === 0}
                        />
                    );
                },
                Cell: (row: any) => {
                    const hasTimesheet =
                        row.original.booking?.timesheet_id != null ||
                        row.original.booking?.timesheet_number != null;
                    const wasGenerated =
                        this.props.generatedBookings?.includes(
                            row.original.booking?.id,
                        ) || false;

                    const clientEligibility = this.getClientTimesheetEligibility(
                        row.original,
                    );
                    const clientTimesheetGenerationTurnedOn =
                        clientEligibility.isEligible;

                    const isDisabled =
                        hasTimesheet ||
                        wasGenerated ||
                        !clientTimesheetGenerationTurnedOn;

                    const messages = [];
                    if (hasTimesheet || wasGenerated) {
                        messages.push('This booking is already assigned to a timesheet');
                    } else if (!clientTimesheetGenerationTurnedOn) {
                        if (!clientEligibility.clientElectronicAuthorisation) {
                            messages.push('Client electronic authorisation is off.');
                        }
                        if (!clientEligibility.clientHasReferralCode) {
                            messages.push('Client has no referral code set.');
                        }
                        if (!clientEligibility.clientNotOnStop) {
                            messages.push('Client is on stop.');
                        }
                        if (!clientEligibility.clientHasApprovers) {
                            messages.push('Client has no approvers set.');
                        }
                    }
                    return (
                        <input
                            type="checkbox"
                            checked={selectedRows.includes(row.original.id)}
                            disabled={isDisabled}
                            onChange={() =>
                                this.toggleRowSelection(row.original.id)
                            }
                            title={messages.length > 0 ? messages.join('\n') : ''}
                        />
                    );
                },
                sortable: false,
                width: 45,
                style: { textAlign: 'center' },
            },
            {
                id: 'ref',
                Header: () => (
                    <span
                        ref={el => {
                            this.row = el;
                        }}
                    >
                        Ref
                    </span>
                ),
                accessor: (row: TJobItem) => row.booking?.reference_number,
                headerStyle: { paddingLeft: '19px' },
                width: 110,
                style: { paddingLeft: '19px' },
                Cell: (row: any) => (
                    <a
                        className="group-link"
                        href={`/operations/bookings?page=1&getall=1&search=${row.value}`}
                    >
                        {row.value}
                    </a>
                ),
            },
            {
                id: 'clientName',
                Header: 'Client',
                accessor: (row: TJobItem) => row.client_full.company_name,
                Cell: (row: any) => (
                    <a
                        className="group-link"
                        target="_blank"
                        href={`/operations/client/${row.original.client_full?.id}`} rel="noreferrer"
                    >
                        {row.value}
                    </a>
                ),
                width: 150,
            },
            {
                Header: 'Date',
                accessor: 'date',
                maxWidth: 80,
                Cell: (row: any) => {
                    const date = moment(row.value).format('DD/MM/YYYY');
                    return date;
                },
            },
            {
                id: 'startTime',
                Header: 'Start Time',
                headerStyle: { paddingLeft: '6px' },
                width: 110,
                sortable: false,
                accessor: (row: TJobItem) => row.booking?.clock_in || '',
                Cell: (row: any) => (
                    <div className="summary-clockin">
                        <div className="start-time__row">
                            <BookingStartTimeWidget
                                job={row.original}
                                notification={this.props.notification}
                                history={history}
                                table={this.props.table}
                                enableModal={true}
                                enableLinkToModal={true}
                                isPreviewMode={false}
                            />
                        </div>
                    </div>
                ),
            },
            {
                id: 'endTime',
                Header: 'End Time',
                headerStyle: { paddingLeft: '6px' },
                width: 110,
                sortable: false,
                accessor: (row: TJobItem) => row.booking?.clock_out || '',
                Cell: (row: any) => {
                    const { isClient } = this.props.user;
                    return (
                        <div className="summary-clockout">
                            <ModalRoute
                                id={row.original.booking.id}
                                path={`${this.props.match.path}/clockin-out/${row.original.booking.id}`}
                                component={ConflictTimesContainer}
                                linkClassName="clock-link"
                                className={
                                    isClient
                                        ? 'react-router-modal__modal clock-out-client'
                                        : 'react-router-modal__modal'
                                }
                                props={{
                                    job: row.original,
                                    notification: this.props.notification,
                                    isClient,
                                    table: this.props.table,
                                    history,
                                    reloadData: () =>
                                        this.getData(
                                            this.buildQueryString(
                                                this.state.query,
                                            ),
                                        ),
                                    mode: 'payroll',
                                }}
                                onBackdropClick={() => {
                                    const { id } = row.original.booking;
                                    const pathname =
                                        this.props.location.pathname.replace(
                                            `clockin-out/${id}`,
                                            '',
                                        );
                                    history.push(
                                        pathname + this.props.location.search,
                                    );
                                }}
                            />
                            <div className="end-time__row">
                                <BookingEndTimeWidget
                                    job={row.original}
                                    notification={this.props.notification}
                                    history={history}
                                    table={this.props.table}
                                    enableModal={false}
                                    enableLinkToModal={true}
                                    isPreviewMode={false}
                                />
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: 'Duration',
                accessor: 'duration',
                width: 90,
                Cell: (row: any) => {
                    const { booking } = row.original;
                    const startTime = moment(
                        booking.datetime_started || row.original.start_datetime,
                    );
                    const finishTime = moment(
                        booking.datetime_finished || row.original.end_datetime,
                    );
                    const duration = finishTime.diff(startTime, 'hours', true);
                    return (
                        <span>{parseFloat(duration.toFixed(2)) || '-'}</span>
                    );
                },
            },
            {
                id: 'hourlyRate',
                Header: 'Hourly Rate',
                accessor: (row: TJobItem) =>
                    row.booking?.freelancer_pay_per_hour,
                width: 110,
                HeaderStyle: { paddingRight: '24px' },
                Cell: (row: any) =>
                    row.value > 0 ? <span>£{row.value.toFixed(2)}</span> : '-',
            },
            {
                id: 'shiftPay',
                Header: 'Shift Pay',
                accessor: (row: TJobItem) => row.booking?.freelancer_total_pay,
                width: 110,
                Cell: (row: any) =>
                    row.value > 0 ? <span>£{row.value.toFixed(2)}</span> : '-',
            },
            {
                id: 'shiftBreak',
                Header: 'Break (in min)',
                accessor: (row: TJobItem) => row.booking?.breaks,
                width: 110,
                Cell: (row: any) =>
                    row.value > 0 ? <span>{row.value}</span> : '-',
            },
            {
                id: 'timesheetNumber',
                Header: 'Timesheet Number',
                accessor: (row: TJobItem) =>
                    row.booking?.timesheet_number ?? '-',
                width: 150,
                Cell: (row: any) => {
                    const timesheetNumber = row.original.booking?.timesheet_number;
                    const timesheetId = row.original.booking?.timesheet_id;
                    const hasRejected = row.original.booking?.has_rejected_timesheets;

                    if (!timesheetNumber) {
                        return '-';
                    }

                    return (
                        <span className="timesheet-number-cell">
                            <button
                                type="button"
                                className="timesheet-number-link group-link"
                                onClick={() => this.openTimesheetModal(timesheetId, hasRejected)}
                            >
                                {timesheetNumber}
                            </button>
                            {hasRejected ? (
                                <Tooltip tooltip="A client rejected timesheet for this shift">
                                    <i className="icon icon-error error-icon" />
                                </Tooltip>
                            ) : null}
                        </span>
                    );
                },
            },
            {
                id: 'timesheetStatus',
                Header: 'Timesheet Status',
                accessor: (row: TJobItem) =>
                    row.booking?.timesheet_status ?? '-',
                width: 250,
            },
        ];

        let hasPagination = false;
        let pageSize = 0;
        let pages = -1;
        if (content && meta) {
            pages = Math.ceil(meta.count / 10);
            pageSize = content.length;
            if (meta.count > 10) {
                hasPagination = true;
            }
        }
        return (
            <div className="payroll-subcomponent">
                <ReactTable
                    data={content}
                    columns={columns}
                    showPageSizeOptions={false}
                    showPaginationBottom={hasPagination}
                    page={currentPage}
                    pageSize={pageSize}
                    pages={pages}
                    loading={loading}
                    getProps={this.getProps}
                    getTrProps={this.getTrProps}
                    getTdProps={this.getTdProps}
                    getNoDataProps={this.getNoDataProps}
                    onPageChange={this.onPageChange}
                    manual={true}
                    sortable={false}
                />
                {this.state.selectedTimesheetId != null && (
                    <TimesheetDetailsModal
                        isOpen={this.state.isTimesheetModalOpen}
                        timesheetId={this.state.selectedTimesheetId}
                        onClose={this.closeTimesheetModal}
                        allowEdit={!!this.state.selectedTimesheetEditable}
                        rateOptions={this.props.rateOptions}
                        notification={this.props.notification}
                        onSaved={() => {
                            this.closeTimesheetModal();
                            this.reloadSubData();
                            this.props.onTimesheetUpdated?.();
                        }}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: TRootState) => ({
    user: state.user,
});

export default connect(mapStateToProps)(PayrollSubComponent);

